import React from 'react';
import { Descriptions, Tag } from 'antd';
import type { Organization } from '@/api/modules/organization';
import { OrganizationType } from '@/api/modules/organization';

const TYPE_MAP: Record<number, { text: string; color: string }> = {
  [OrganizationType.COMPANY]:    { text: '公司', color: 'purple' },
  [OrganizationType.DEPARTMENT]: { text: '部门', color: 'blue' },
  [OrganizationType.GROUP]:      { text: '小组', color: 'green' },
  [OrganizationType.TEAM]:       { text: '团队', color: 'orange' },
};

const RELATION_LABELS: Record<string, string> = {
  headquarters: '总部',
  subsidiary: '子公司',
  branch: '分公司',
  division: '事业部',
  region: '区域',
  store: '门店',
  direct_store: '直营店',
  franchise: '加盟店',
  dealer: '经销商',
  distributor: '分销商',
  agent: '代理商',
  ecommerce: '电商渠道',
  supplier: '供应商',
  manufacturer: '制造商',
  logistics: '物流商',
  warehouse: '仓库',
  customer: '客户',
  partner: '合作伙伴',
};

const LEVEL_TEXT: Record<number, string> = { 1: '一级', 2: '二级', 3: '三级', 4: '四级' };

interface Props {
  org: Organization;
}

const OrganizationDetail: React.FC<Props> = ({ org }) => {
  const typeConf = TYPE_MAP[org.type as unknown as number];
  const relationTypes = ((org as any).relationTypes ?? (org as any).relation_types ?? []) as string[];
  const isActive = org.status === 'active';

  return (
    <Descriptions column={2} bordered size="small">
      <Descriptions.Item label="组织名称">{org.name}</Descriptions.Item>
      <Descriptions.Item label="组织编码">{org.code}</Descriptions.Item>
      <Descriptions.Item label="组织类型">
        {typeConf
          ? <Tag color={typeConf.color}>{typeConf.text}</Tag>
          : relationTypes.length > 0
            ? relationTypes.map(type => <Tag key={type}>{RELATION_LABELS[type] || type}</Tag>)
            : '-'}
      </Descriptions.Item>
      <Descriptions.Item label="组织层级">
        {LEVEL_TEXT[org.level] || `${org.level}级`}
      </Descriptions.Item>
      <Descriptions.Item label="负责人">
        {(org as any).leaderName || org.leader_name || '-'}
      </Descriptions.Item>
      <Descriptions.Item label="成员人数">
        {(org as any).memberCount ?? org.member_count ?? 0} 人
      </Descriptions.Item>
      <Descriptions.Item label="排序">{(org as any).sort ?? (org as any).sort_order ?? '-'}</Descriptions.Item>
      <Descriptions.Item label="状态">
        <Tag color={isActive ? 'success' : 'error'}>{isActive ? '正常' : '停用'}</Tag>
      </Descriptions.Item>
      <Descriptions.Item label="联系电话">{org.phone || (org as any).contact_phone || '-'}</Descriptions.Item>
      <Descriptions.Item label="邮箱地址">{org.email || (org as any).contact_email || '-'}</Descriptions.Item>
      <Descriptions.Item label="创建时间">
        {(org as any).createdAt || org.created_at}
      </Descriptions.Item>
      <Descriptions.Item label="更新时间">
        {(org as any).updatedAt || org.updated_at}
      </Descriptions.Item>
      <Descriptions.Item label="办公地址" span={2}>{org.address || '-'}</Descriptions.Item>
      <Descriptions.Item label="组织描述" span={2}>{org.description || (org as any).remark || '-'}</Descriptions.Item>
    </Descriptions>
  );
};

export default OrganizationDetail;
